/**
 * Compact auto-cycling feature card for the login page on mobile.
 * Shares slide content with LoginSlider via loginSliderShared module.
 */

import { useEffect, useRef, useState } from 'react';

import { useTranslate } from 'hooks/useTranslate';

import {
  generateLoginSlides,
  getSlideShortBody,
  LOGIN_SLIDER_INTERVAL_MS,
  LOGIN_SLIDER_MOBILE_FADE_MS,
  LoginSliderMobileProps,
  useLoginSliderCycle,
} from 'modules/auth/pages/Login/loginSliderShared';

import './LoginSliderMobile.scss';

type SlideAnimationPhase = 'entering' | 'visible' | 'exiting';

export default function LoginSliderMobile({
  dark = false,
  intervalMs = LOGIN_SLIDER_INTERVAL_MS,
  className,
  style,
}: LoginSliderMobileProps) {
  const { t } = useTranslate();
  const slides = generateLoginSlides(t);
  const { idx, visible } = useLoginSliderCycle({
    slideCount: slides.length,
    intervalMs,
    fadeMs: LOGIN_SLIDER_MOBILE_FADE_MS,
  });
  const [displayIdx, setDisplayIdx] = useState(0);
  const [phase, setPhase] = useState<SlideAnimationPhase>('visible');
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setDisplayIdx(idx);
      setPhase('visible');
      return undefined;
    }

    if (!visible) {
      setPhase('exiting');
      return undefined;
    }

    setDisplayIdx(idx);
    setPhase('entering');
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPhase('visible'));
    });
    return () => cancelAnimationFrame(frameId);
  }, [visible, idx]);

  const slide = slides[displayIdx];
  const shortBody = getSlideShortBody(slide.body);

  const cardBg = dark ? 'rgba(255,255,255,0.05)' : `${slide.color}0d`;
  const cardBdr = dark
    ? '1px solid rgba(255,255,255,0.09)'
    : `1px solid ${slide.color}28`;
  const titleColor = dark ? '#fff' : '#111729';
  const bodyColor = dark ? 'rgba(255,255,255,0.55)' : '#718096';
  const inactiveDot = dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)';

  return (
    <div className={className} style={{ margin: '28px 0 0', ...style }}>
      <div
        style={{
          background: cardBg,
          border: cardBdr,
          borderRadius: 10,
          padding: '18px 20px',
          transition: 'background 0.6s ease, border-color 0.6s ease',
        }}
      >
        <div className="login-slider-mobile__content">
          <div className={`login-slider-mobile__slide is-${phase}`}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                flexShrink: 0,
                background: `${slide.color}22`,
                border: `1px solid ${slide.color}44`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 0 16px ${slide.color}33`,
              }}
            >
              <slide.SlideIcon size={20} color={slide.color} strokeWidth={1.8} />
            </div>

            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: titleColor,
                  lineHeight: 1.3,
                  marginBottom: 3,
                }}
              >
                {slide.title}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: bodyColor,
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {shortBody}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 6,
            marginTop: 14,
            justifyContent: 'center',
          }}
        >
          {slides.map((slideItem, i) => (
            <div
              key={slideItem.key}
              style={{
                width: i === idx ? 20 : 6,
                height: 5,
                borderRadius: i === idx ? 3 : '50%',
                background: i === idx ? slide.color : inactiveDot,
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
